import type React from 'react';
import { type JSX, useEffect } from 'react';
import type { Hub, TopicHubPage, TopicPackage, TopicPackageNativo, TopicPost } from 'interfaces/Home';
import TopicHero from 'components/TopicHubPage/TopicHero';
import TopicPackageItem from 'components/TopicPackage';
import ErrorBoundary from 'components/ErrorBoundary';
import HubHeader from 'components/HubHeader';
import type { MenuItemProps } from 'interfaces/navigation/Header';
import { UIFragments } from 'utils/log/constants/uiFragments';
import type { PresentedByProps } from 'components/PresentedBy';
import * as S from './TopicHubPagesStyles';
import ArticleGrid from 'components/Grid/ArticleGrid/ArticleGrid';
import { PageType } from 'interfaces/content/articles/Post';
import type { PagePagination } from 'utils/miscUtils/calculatePageInfo';
import generateNativoContent, { generateNativoLatest } from 'utils/miscUtils/nativoHelpers';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import ArticleCard from 'components/Globals/ArticleCard';
import { getRowNumber } from 'utils/miscUtils/nativoHelpers';
import useBreakpoint from 'hooks/useBreakpoint';
import { RightRailFlex } from 'components/Ad/AdSlot';
import { RightRailStickyAd } from 'components/Ad/AdSlot/Styles';
import AdSlotToggle from 'components/Ad/AdSlot/AdSlotToggle';
import getCommonTestId from 'constants/testsIds/common';
import Pagination from 'components/Pagination/Pagination';
import SectionTitle from 'components/Globals/SectionTitle';
import * as RR from 'styles/rightRail';
import MoreRankings from 'components/Ranking/MoreRankings';
import type { RelatedRanking } from 'interfaces/content/Ranking';
import Banner from 'components/Banner';
import Breadcrumbs from 'components/Breadcrumbs';
import type { BreadcrumbsProps } from 'interfaces/navigation/Breadcrumbs';
import InStream from 'components/Ad/AdSlot/InStream';
import { useScrollToElement } from 'hooks/useScrollToElement';
import { useRouter } from 'next/router';
import { useHeaderHeight } from 'hooks/useHeaderHeight';
import cx from 'classnames';
import NativoPlaceholder from 'components/Nativo/NativoPlaceholder';
import TopicRanking from 'components/TopicRankings/TopicRanking';
import ArticleSection from 'components/Sections/ArticleSection';
import Link from 'components/Globals/Base/Link';

export interface TopicHubPageProps {
  data: TopicHubPage;
  topicPackages?: TopicPackage[];
  rankingPanel?: [];
  subMenu?: MenuItemProps[];
  presentedBy?: PresentedByProps;
  relatedRankings: RelatedRanking[];
  sidebar?: (JSX.Element | null)[];
  pageSize: number;
  pagination?: PagePagination;
  hideAds?: boolean;
  topicPosts?: TopicPost;
}

const TopicHubPages: React.FC<TopicHubPageProps> = ({
  data,
  topicPackages,
  subMenu,
  presentedBy,
  relatedRankings,
  pagination,
  pageSize,
  hideAds,
  sidebar,
  rankingPanel,
  topicPosts,
}) => {
  const instanceOfTopicHub = (object: any): object is TopicHubPage => 'sponsor' in object;
  const IsNativoTopic = instanceOfTopicHub(data) && data.nativo?.nativo_enable;

  let NativoHubPosts: JSX.Element[] = [];
  let NativoHubLatest: JSX.Element[] = [];
  const isMobile = useBreakpoint('mdMax');
  const isDesktop = useBreakpoint('hybridMin');
  const isSmallScreen = useBreakpoint('mdMin');
  const [executeScroll, elRef] = useScrollToElement<HTMLHeadingElement>();
  const router = useRouter();
  const { isReady } = router;

  const isFirstPage = pagination.currentPage === 1;
  const { headerHeight } = useHeaderHeight();

  useEffect(() => {
    if (!isFirstPage && isReady) {
      executeScroll();
    }
  }, [isFirstPage, isReady, executeScroll]);

  let nativoAdPosition = isSmallScreen ? 2 : 1;

  const { nativo } = data as Hub;
  const hasNativo = nativo?.nativo_enable;
  const hasTopicImage =
    (typeof data.image !== 'string' && data.image?.mediaItemUrl) || (typeof data.image === 'string' && data.image);
  const hasTopicDescription = data.headline !== '' || data.description.trim() !== '';

  const islatestEnable = data.latestEnable;
  const shouldRenderRankings = data.f500Rankings;

  const showAdSlot =
    topicPackages.length > 0 || hasTopicImage || islatestEnable || shouldRenderRankings || relatedRankings?.length > 0;

  const showMoreTitle = data.showMoreTitle ?? true;
  const dataCy = isDesktop ? 'sectionRightRail' : '';
  const breadcrumbsData = (data as Hub).breadcrumbs?.items.length
    ? [{ label: 'home', link: '/' }, ...((data as Hub).breadcrumbs as BreadcrumbsProps).items]
    : [
        { label: 'home', link: '/' },
        { label: data.name, link: data.link },
      ];
  const topicPackagesNativo = [];

  topicPackages.forEach((pack, idx) => {
    const nativoCategory = {
      articles: [],
      customUrl: pack.customUrl,
      databaseId: pack.databaseId,
      description: pack.description,
      image: pack.image,
      isMagazine: pack.isMagazine,
      link: pack.link,
      name: pack.name,
      permalink: pack.permalink,
      slug: pack.slug,
    };
    nativoAdPosition = pack.isMagazine === true ? 1 : 2;

    if (isMobile) nativoAdPosition = 1;
    const nativoChildrenPosts: JSX.Element[] = [];
    const nativeplaceHolderClass = pack.articles.length <= 3 && !isMobile ? 'nativo-320-placeholder' : '';

    pack.articles.forEach((article, index) => {
      const articleCard = (
        <ArticleCard
          key={article.title}
          layoutVariant='hero'
          eyebrowType='red'
          article={article}
          trackingData={{
            contentPlacementCD: 'hero',
            eventAction: 'title click',
            eventCategory: 'navigation',
            eventLabel: article.title,
          }}
          titleFontFamily='saol'
          titleSize={{ default: 'level2', mdMin: 'level1' }}
          className='article-card'
          priority
        />
      );
      if (index === nativoAdPosition && hasNativo) {
        nativoChildrenPosts.push(
          <div className='article-card'>
            <NativoPlaceholder
              className={`nativo-topic-${idx + 1} ${nativeplaceHolderClass}`}
              key={`nativo-${idx + 1}`}
            />
          </div>,
        );
      }
      nativoChildrenPosts.push(articleCard);
    });
    if (pack.articles.length <= nativoAdPosition && hasNativo) {
      nativoChildrenPosts.push(
        <div className='article-card'>
          <NativoPlaceholder
            className={`nativo-topic-${idx + 1} ${nativeplaceHolderClass}`}
            key={`nativo-${idx + 1}`}
          />
        </div>,
      );
    }
    nativoCategory.articles = nativoChildrenPosts;
    topicPackagesNativo.push(nativoCategory);
  });

  const latestArticles = islatestEnable ? topicPosts?.posts.slice(0, 6) : [];
  if (IsNativoTopic) {
    NativoHubPosts = generateNativoContent(topicPosts?.posts, false, data.name, isDesktop, isMobile);
    NativoHubLatest = generateNativoLatest(latestArticles, isSmallScreen);
  }

  return (
    <S.Content className={cx({ 'small-gap': !showMoreTitle })}>
      <Breadcrumbs
        isHubPage
        items={breadcrumbsData}
      />
      <div>
        <ErrorBoundary fragment={UIFragments.TOPIC_PAGE}>
          <S.Header>
            <HubHeader
              title={data.name}
              subMenu={subMenu}
              sponsor={
                presentedBy
                  ? {
                      ...presentedBy,
                      link: presentedBy.linkTo || '',
                      sponsorEnabled: (instanceOfTopicHub(data) && data.sponsor?.sponsorEnable) || false,
                      thumbnail: typeof presentedBy.logo === 'string' ? presentedBy.logo : '',
                    }
                  : undefined
              }
              pageInfo={!showMoreTitle && pagination.currentPage > 1 ? pagination.pageInfo : ''}
            />
          </S.Header>
        </ErrorBoundary>
        <S.HeroWrapper>
          {(hasTopicImage || hasTopicDescription) && (
            <TopicHero
              image={data.image}
              description={data.description}
              headline={data.headline}
            />
          )}
        </S.HeroWrapper>
      </div>
      {shouldRenderRankings && <TopicRanking rankingPanel={rankingPanel} />}
      {islatestEnable && latestArticles.length > 0 && (
        <S.LatestWrapper data-cy={getCommonTestId('TOPIC_PAGE_LATEST_MODULE')}>
          <S.HeaderContainer>
            <SectionTitle
              tag='h2'
              text={`Latest ${data.name} Coverage`}
              className='category-title'
              variant='border'
              titleDataCy={getCommonTestId('TOPIC_PAGE_LATEST_TITLE')}
            />
            <S.MoreLink mobileOnly={false}>
              <Link
                href='#moreCoverage'
                type='tertiaryButton'
                className='more-link'
                data-cy={getCommonTestId('TOPIC_PAGE_LATEST_MORE_BUTTON')}
              >
                More in {data.name}
              </Link>
            </S.MoreLink>
          </S.HeaderContainer>
          <ArticleSection
            sponsor={hasNativo}
            contentWithAd={NativoHubLatest}
            latestArticles={latestArticles}
          />
        </S.LatestWrapper>
      )}
      <ErrorBoundary fragment={UIFragments.HUB_ARTICLE_BANNER}>
        {data.banner?.enabled && (
          <Banner
            enabled={data.banner.enabled}
            desktop={data.banner.bannerDesktop}
            mobile={data.banner.bannerMobile}
            link={data.banner.link}
          />
        )}
      </ErrorBoundary>
      {topicPackagesNativo &&
        topicPackagesNativo?.length > 0 &&
        topicPackagesNativo?.map((packageItem: TopicPackageNativo) => (
          <TopicPackageItem
            key={packageItem.name}
            packageItem={packageItem}
            className='topic-package'
          />
        ))}
      {relatedRankings?.length > 0 && <MoreRankings relatedRankings={relatedRankings.slice(0, 3)} />}
      {showAdSlot && (
        <InStream
          index={0}
          renderAds={!hideAds}
        />
      )}
      {topicPosts?.posts && topicPosts?.posts?.length > 0 && (
        <S.BodyWrapper id='moreCoverage'>
          <S.ArticleWrapper className='article-wrapper-topic'>
            {showMoreTitle && (
              <ErrorBoundary fragment={UIFragments.TOPIC_PAGE}>
                <S.Header
                  ref={elRef}
                  $headerHeight={headerHeight + 100}
                >
                  <SectionTitle
                    tag='h2'
                    text={`More ${data.name}`}
                    className='title'
                    variant='border'
                  />
                </S.Header>
              </ErrorBoundary>
            )}
            {pagination && pagination.showPageInfo && showMoreTitle && (
              <ErrorBoundary fragment={UIFragments.TOPIC_PAGE}>
                <S.PageInfo
                  as='h4'
                  $size={{ default: 'level7', mdMin: 'level6' }}
                  $fontFamily='graphikCond'
                >
                  {pagination.pageInfo}
                </S.PageInfo>
              </ErrorBoundary>
            )}
            <ErrorBoundary fragment={UIFragments.TOPIC_PAGE}>
              <ArticleGrid
                pageType={PageType.TOPIC}
                pageSize={pageSize}
                hideAds={hideAds}
              >
                {IsNativoTopic
                  ? NativoHubPosts
                  : topicPosts?.posts?.map((post: ArticlePreview, index: number) => (
                      <ArticleCard
                        key={post.databaseId}
                        article={post}
                        layoutVariant='hero'
                        dataVariant={'hub'}
                        eyebrowType='red'
                        titleSize={{ default: 'level3' }}
                        priority={isMobile && index === 0}
                        trackingData={{
                          contentPlacementCD: `${data.name} grid - row ${getRowNumber(index, isDesktop, isMobile) + 1}`,
                          eventAction: 'title click',
                          eventCategory: 'navigation',
                          eventLabel: post.title,
                        }}
                      />
                    ))}
              </ArticleGrid>
            </ErrorBoundary>
            {pagination && pagination.pagesCount > 0 && (
              <ErrorBoundary fragment={UIFragments.TOPIC_PAGE}>
                <Pagination pagination={pagination} />
              </ErrorBoundary>
            )}
          </S.ArticleWrapper>

          <ErrorBoundary fragment={UIFragments.TOPIC_PAGE}>
            <RR.RightRail
              data-cy={dataCy}
              className={cx('right-rail-ad-container', { 'small-gap': !showMoreTitle })}
            >
              {!hideAds && (
                <RightRailFlex
                  id={0}
                  dataCy={getCommonTestId('RIGHT_RAIL_AD')}
                />
              )}
              {sidebar}
              {isDesktop && !hideAds && (
                <RightRailStickyAd
                  data-cy={getCommonTestId('RIGHT_RAIL_STICKY_AD_HUB')}
                  className='right-rail-ad'
                >
                  <AdSlotToggle
                    placementName='RightRailFlex'
                    sticky
                    index={3}
                    slotId={`RightRailFlex${3}`}
                  />
                </RightRailStickyAd>
              )}
            </RR.RightRail>
          </ErrorBoundary>
        </S.BodyWrapper>
      )}
    </S.Content>
  );
};

export default TopicHubPages;
